import { useState, useEffect, useCallback } from 'react';
import type { RiderAccountInfo, BankOption } from '../types/riderInfoDetail.types';
import { riderInfoDetailApi } from '../api/riderInfoDetailApi';
import { BANK_OPTIONS } from '../constants/riderInfoDetail.constants';
import {
    validateAccountInfo,
    sanitizeAccountNumber,
    sanitizeAccountHolder,
} from '../utils/riderInfoDetailHelpers';
import { useToast } from '@/shared/components/feedback/Toast/ToastContext';
import { ToastType } from '@/shared/components/feedback/Toast/Toast.types';
import { commonCodeApi } from '@/shared/api/commonCodeApi';

export const useRiderAccountInfo = (riderId?: string, initialAccountInfo?: RiderAccountInfo) => {
    const { showToast } = useToast();
    const [accountInfo, setAccountInfo] = useState<RiderAccountInfo>({
        bank: '',
        accountNumber: '',
        accountHolder: '',
    });
    const [bankOptions, setBankOptions] = useState<readonly BankOption[]>([
        { value: '', label: '선택' },
        ...BANK_OPTIONS.slice(1),
    ]);

    useEffect(() => {
        if (initialAccountInfo && (initialAccountInfo.bank || initialAccountInfo.accountNumber)) {
            setAccountInfo(initialAccountInfo);
        }
    }, [initialAccountInfo]);

    const fetchBankOptions = useCallback(async () => {
        try {
            const response = await commonCodeApi.getCommonCodeList('BANK_CODE');
            if (response?.codes && Array.isArray(response.codes)) {
                setBankOptions([
                    { value: '', label: '선택' },
                    ...response.codes
                        .filter((code) => code.useYn === 'Y')
                        .map((code) => ({ value: code.comCode, label: code.comCdName })),
                ]);
            }
        } catch {
            setBankOptions([{ value: '', label: '선택' }, ...BANK_OPTIONS.slice(1)]);
        }
    }, []);

    useEffect(() => {
        fetchBankOptions();
    }, [fetchBankOptions]);

    const handleSaveAccount = useCallback(() => {
        const validationError = validateAccountInfo(
            accountInfo.bank,
            accountInfo.accountNumber,
            accountInfo.accountHolder,
        );
        if (validationError) {
            showToast(validationError, { type: ToastType.WARNING });
            return;
        }

        const save = async () => {
            try {
                if (!riderId) {
                    showToast('라이더 ID가 없습니다.', { type: ToastType.ERROR });
                    return;
                }
                await riderInfoDetailApi.updateRiderAccount(riderId, {
                    riderId,
                    centerId: null,
                    accountNum: accountInfo.accountNumber,
                    accName: accountInfo.accountHolder,
                    bankCode: accountInfo.bank,
                    dpAcctNum: null,
                    dpBankCode: null,
                    dpAcctName: null,
                });
                const updatedData = await riderInfoDetailApi.getRiderDetail(riderId);
                if (updatedData.accountNum || updatedData.bankCode) {
                    setAccountInfo({
                        bank: updatedData.bankCode || '',
                        accountNumber: updatedData.accountNum || '',
                        accountHolder: updatedData.accName || '',
                    });
                }
                showToast('계좌 정보가 성공적으로 저장되었습니다.', { type: ToastType.SUCCESS });
            } catch {
                showToast('계좌 정보 저장에 실패했습니다. 다시 시도해주세요.', { type: ToastType.ERROR });
            }
        };
        save();
    }, [riderId, accountInfo, showToast]);

    const handleAccountNumberChange = useCallback((value: string) => {
        setAccountInfo((prev) => ({ ...prev, accountNumber: sanitizeAccountNumber(value) }));
    }, []);

    const handleAccountHolderChange = useCallback((value: string) => {
        setAccountInfo((prev) => ({ ...prev, accountHolder: sanitizeAccountHolder(value) }));
    }, []);

    return {
        accountInfo,
        setAccountInfo,
        bankOptions,
        handleSaveAccount,
        handleAccountNumberChange,
        handleAccountHolderChange,
    };
};
